import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import { useCurrentOrganization } from '@/hooks/use-current-organization';
import { getOrganizationClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';
import { Button } from '@/components/ui/button';
import { SaveButton } from '@/components/custom/save-button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

export default function OrganizationPage(): React.ReactElement {
  const { data: organization, isLoading } = useCurrentOrganization();
  const queryClient = useQueryClient();

  // Form state
  const [name, setName] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [inboundPhone, setInboundPhone] = useState('');
  const [outboundPhone, setOutboundPhone] = useState('');
  const [practiceDescription, setPracticeDescription] = useState('');

  // Sync form state with organization data
  useEffect(() => {
    if (organization) {
      setName(organization.name ?? '');
      setFromEmail(organization.fromEmail ?? '');
      const settings = organization.settings as Record<string, unknown> | null;
      setInboundPhone((settings?.inboundPhone as string) ?? '');
      setOutboundPhone((settings?.outboundPhone as string) ?? '');
      setPracticeDescription((settings?.practiceDescription as string) ?? '');
    }
  }, [organization]);

  // Update mutation
  const updateOrganization = useMutation({
    mutationFn: async (data: {
      name?: string;
      fromEmail?: string | null;
      settings?: Record<string, unknown>;
    }) => {
      return getOrganizationClient().update(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.current() });
    },
  });

  const handleSave = async () => {
    const existingSettings = (organization?.settings as Record<string, unknown>) ?? {};
    await updateOrganization.mutateAsync({
      name: name || undefined,
      fromEmail: fromEmail || null,
      settings: {
        ...existingSettings,
        inboundPhone: inboundPhone || undefined,
        outboundPhone: outboundPhone || undefined,
        practiceDescription: practiceDescription.trim() || undefined,
      },
    });
  };

  const currentSettings = (organization?.settings as Record<string, unknown> | null) ?? {};
  const hasChanges =
    name !== (organization?.name ?? '') ||
    fromEmail !== (organization?.fromEmail ?? '') ||
    inboundPhone !== ((currentSettings.inboundPhone as string) ?? '') ||
    outboundPhone !== ((currentSettings.outboundPhone as string) ?? '') ||
    practiceDescription !== ((currentSettings.practiceDescription as string) ?? '');

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <h2 className="text-lg font-semibold">Organization</h2>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Organization</h2>
          <p className="text-sm text-muted-foreground">Manage your organization settings</p>
        </div>
        <SaveButton
          type="button"
          onClick={handleSave}
          isPending={updateOrganization.isPending}
          isEdit
          disabled={!hasChanges}
        />
      </div>

      <div className="space-y-6">
        {/* General Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              General Settings
            </CardTitle>
            <CardDescription>Basic information about your organization</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Organization Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter organization name"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="fromEmail">From Email</Label>
              <Input
                id="fromEmail"
                type="email"
                value={fromEmail}
                onChange={(e) => setFromEmail(e.target.value)}
                placeholder="noreply@yourpractice.com"
              />
              <p className="text-xs text-muted-foreground">
                Email address used as the sender for notifications and reminders
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Voice Agent Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Voice Agent Settings</CardTitle>
            <CardDescription>Configure settings for voice calls</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="inboundPhone">Inbound Phone Number</Label>
              <Input
                id="inboundPhone"
                type="tel"
                value={inboundPhone}
                onChange={(e) => setInboundPhone(e.target.value)}
                placeholder="+15551234567"
              />
              <p className="text-xs text-muted-foreground">
                The phone number that patients call to reach your voice agent. Format: +1XXXXXXXXXX
              </p>
            </div>

            <Separator />

            <div className="grid gap-2">
              <Label htmlFor="outboundPhone">Outbound Phone Number</Label>
              <Input
                id="outboundPhone"
                type="tel"
                value={outboundPhone}
                onChange={(e) => setOutboundPhone(e.target.value)}
                placeholder="+15551234567"
              />
              <p className="text-xs text-muted-foreground">
                The phone number used as caller ID for outbound calls. Format: +1XXXXXXXXXX
              </p>
            </div>

            <Separator />

            <div className="grid gap-2">
              <Label htmlFor="practiceDescription">What Kind of Practice</Label>
              <Textarea
                id="practiceDescription"
                value={practiceDescription}
                onChange={(e) => setPracticeDescription(e.target.value)}
                placeholder="a lung and sleep specialist office"
                rows={2}
              />
              <p className="text-xs text-muted-foreground">
                A short, plain-language description of your practice for the average patient. The
                voice agent states this if a patient asks &ldquo;what kind of doctor is this?&rdquo;
                Use everyday words (e.g. &ldquo;a lung and sleep specialist office&rdquo;), not
                clinical jargon. Leave blank to have the agent not volunteer a practice type.
              </p>
            </div>
          </CardContent>
        </Card>

        {updateOrganization.isSuccess && (
          <p className="text-sm text-green-600">Settings saved successfully!</p>
        )}

        {updateOrganization.isError && (
          <p className="text-sm text-destructive">Failed to save settings. Please try again.</p>
        )}
      </div>
    </div>
  );
}
